import { z } from "zod";

import { contractSchema, userSchema } from "../commons";

export const gigSummarySchema = z.object({
  _id: z.string(),
  avatar: z.string(),
  name: z.string(),
  company: z.string(),
  totalHours: z.number().optional(),
  totalEarned: z.string().optional(),
});

export type GigSummary = z.infer<typeof gigSummarySchema>;

export const summaryQuerySchema = z.object({
  sort: z.string().optional(),
});

export const summaryRequestSchema = z.object({
  query: summaryQuerySchema,
});

export type SummaryRequest = z.infer<typeof summaryRequestSchema>;

export const summaryResponseSchema = z.object({
  active: z.array(gigSummarySchema),
  inactive: z.array(gigSummarySchema),
});

export type SummaryResponse = z.infer<typeof summaryResponseSchema>;

export const createBodySchema = z.object({
  talent: z.string().nonempty(),
  meetingId: z.string().optional(),
});

export type CreateBody = z.infer<typeof createBodySchema>;

export const createRequestSchema = z.object({
  body: createBodySchema,
});

export type CreateRequest = z.infer<typeof createRequestSchema>;

export const detailParamsSchema = z.object({
  gigID: z.string().nonempty(),
});

export const detailRequestSchema = z.object({
  params: detailParamsSchema,
});

export type DetailRequest = z.infer<typeof detailRequestSchema>;

export const detailResponseSchema = z.object({
  totalHours: z.string(),
  totalBudget: z.string(),
  totalValue: z.string(),
  totalRemaining: z.string(),
  reportTable: z.object({
    columns: z.array(
      z.object({
        key: z.string(),
        displayName: z.string(),
      }),
    ),
    tableMonths: z.array(z.record(z.unknown())),
  }),
  client: userSchema.partial(),
  designer: z.union([userSchema.partial(), z.null()]),
});

export type DetailResponse = z.infer<typeof detailResponseSchema>;

export const gigReportsResponseSchema = z.object({
  designer: z.object({
    _id: z.string(),
    name: z.string(),
    avatar: z.string(),
    hourlyRate: z.number().optional(),
  }),
  reports: z.array(
    z.object({
      _id: z.string(),
      title: z.string(),
      description: z.string(),
      reportWeek: z.number(),
      reportYear: z.number(),
      bookingsAmount: z.number(),
      hoursSpent: z.number().optional(),
    }),
  ),
  contractHistory: z.array(
    contractSchema.extend({
      startDate: z.union([z.string(), z.coerce.date()]),
      endDate: z.union([z.string(), z.coerce.date()]),
    }),
  ),
});

export type GigReportsResponse = z.infer<typeof gigReportsResponseSchema>;

export const hireParamsSchema = z.object({
  gigID: z.string().nonempty(),
});

export const hireBodySchema = z.object({
  expectedToStartAt: z.coerce.date().optional(),
});

export type HireBody = z.infer<typeof hireBodySchema>;

export const hireRequestSchema = z.object({
  params: hireParamsSchema,
  body: hireBodySchema,
});

export type HireRequest = z.infer<typeof hireRequestSchema>;

export const scheduleInterviewParamsSchema = z.object({
  gigID: z.string().nonempty(),
});

export const scheduleInterviewBodySchema = z.object({
  meetingId: z.string().nonempty(),
});

export type ScheduleInterviewBody = z.infer<typeof scheduleInterviewBodySchema>;

export const scheduleInterviewRequestSchema = z.object({
  params: scheduleInterviewParamsSchema,
  body: scheduleInterviewBodySchema,
});

export type ScheduleInterviewRequest = z.infer<
  typeof scheduleInterviewRequestSchema
>;
